import React from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { useCourses } from '@/hooks/useCourses';
import { AutoMigrationIndicator } from '@/components/courses/AutoMigrationIndicator';
import CourseIntegrationStatus from '@/components/debug/CourseIntegrationStatus';
import ContentSyncDashboard from '@/components/admin/ContentSyncDashboard';
import CoursesMigrationTrigger from '@/components/courses/CoursesMigrationTrigger';
import SystemHealthCheck from '@/components/debug/SystemHealthCheck';
import AuthStatus from '@/components/debug/AuthStatus';
import RBACTester from '@/components/debug/RBACTester';
import AdminAuthDebug from '@/components/debug/AdminAuthDebug';
import CourseAssignmentValidator from '@/components/debug/CourseAssignmentValidator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Shield, Settings, Database, AlertTriangle, FileText } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { StudentProgressReport } from '@/components/reports/StudentProgressReport';

const AdminTools: React.FC = () => {
  const { hasAccess, isLoading } = useSupabaseAuth();
  const { autoMigration } = useCourses();

  // Show loading while checking permissions
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading admin tools...</p>
        </div>
      </div>
    );
  }

  // Redirect non-admin users
  if (!hasAccess(['admin', 'staff'])) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 space-y-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Admin Tools</h1>
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Administrative tools for managing courses, debugging, and system monitoring.
          </p>
        </div>

        {/* Warning Alert */}
        <Alert className="max-w-4xl mx-auto">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            These tools are for administrative use only. Use caution when running migrations or making system changes.
          </AlertDescription>
        </Alert>

        {/* Course Management Section */}
        <div className="max-w-6xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Course Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Auto Migration Indicator */}
              <AutoMigrationIndicator 
                isRunning={autoMigration.isRunning}
                progress={autoMigration.progress}
                total={autoMigration.total}
                completed={autoMigration.completed}
                errors={autoMigration.errors}
                hasRun={autoMigration.hasRun}
              />
              
              {/* Course Integration Status */}
              <CourseIntegrationStatus />
              
              <Separator className="my-6" />
              
              {/* Content Sync Dashboard */}
              <ContentSyncDashboard />
              
              <Separator className="my-6" />
              
              {/* Migration Tool */}
              <CoursesMigrationTrigger />
            </CardContent>
          </Card>

          {/* System Monitoring Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                System Monitoring & Debug
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SystemHealthCheck />
                <AuthStatus />
                <RBACTester />
                <AdminAuthDebug />
                <CourseAssignmentValidator />
              </div>
            </CardContent>
          </Card>

          {/* Reports Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Student Progress Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StudentProgressReport />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminTools;